// Do not import any schemas, constants, or enums from outside this folder
// to prevent circular dependencies

import { extendZodWithOpenApi } from '@asteasolutions/zod-to-openapi';
import dayjs from 'dayjs';
import { z } from 'zod';

import { StaffUserGroups } from './constants';
import {
  Grade,
  GRADE_LABELS,
  Location,
  LOCATION_LABELS,
  Subject,
  SUBJECT_LABELS,
  SubjectLabel,
  ThemeMode,
  USER_GROUP_LABELS,
  UserGroup,
  UserType,
  XeroDepartment,
} from './enums';
import { enumLabels } from './fields';

extendZodWithOpenApi(z);

/**
 * Common Schemas
 */

export const GenericRouteSchema = z.object({
  body: z.record(z.string(), z.any()),
  query: z.record(z.string(), z.any()),
  params: z.record(z.string(), z.any()),
});

export const UnixTimestampSchema = z
  .number()
  .int('Must be an integer timestamp')
  .min(0, 'Timestamp must be positive')
  .refine(val => val < 253402300800000, 'Timestamp is too far in the future')
  .openapi({
    description: 'Unix timestamp in milliseconds',
    example: 1759299922000,
  });

export const SearchStringSchema = z
  .preprocess(val => (val === undefined || val === null ? '' : val), z.coerce.string())
  .transform(val =>
    val
      .toLowerCase()
      .trim()
      .replace(/[, .'"]/g, '')
      .replace(/\+61/g, '0')
  )
  .pipe(z.string().max(255, 'Search string is too long'))
  .openapi({
    description: 'A long search string that is case insensitive and trimmed',
    example: 'johndoe0412345678',
  });

export const HexColorSchema = z
  .string()
  .regex(/^#[0-9A-Fa-f]{6}$/, 'Invalid hex color format')
  .openapi({
    description: 'Hex color code in the format #RRGGBB',
    example: '#0c374d',
  });

export const HttpUrlSchema = z
  .string({ message: 'please enter a link' })
  .transform(val => {
    // If the URL doesn't have any protocol (://), check if it has a TLD and prepend http://
    if (!/^[a-zA-Z][a-zA-Z0-9+.-]*:\/\//.test(val)) {
      return `http://${val}`;
    }
    return val;
  })
  .pipe(
    z
      .url({ protocol: /^https?$/, error: 'must be a valid link' })
      .max(2048, 'is too long to be a link')
  )
  .openapi({
    description:
      'A valid URL starting with http:// or https:// (http:// is prepended if missing to URLs with TLD)',
    example: 'https://example.com/image.png',
  });

export const UUIDSchema = z.uuid('Invalid UUID format').openapi({
  description: 'A valid UUID v4 string',
  example: '550e8400-e29b-41d4-a716-446655440000',
});

// app_users.id and every column or param referencing it: Cognito's IDs are laid out like UUIDs
// but may carry a variant or version digit `z.uuid()` rejects; a Postgres uuid checks neither
export const CognitoUserIdSchema = z.guid('Invalid user ID format').openapi({
  description: "A user's Cognito ID, laid out like a UUID",
  example: '096e8408-8081-70f2-49bc-2e58e3d0a4c9',
  pattern: z.regexes.guid.source,
});

export const AlphanumericLabelSchema = z
  .string()
  .regex(/^[a-zA-Z0-9 ]*$/, 'Label must be alphanumeric')
  .openapi({
    description: 'A label consisting of alphanumeric characters and spaces',
    example: 'Math Homework 1',
  });

export const AllCharLabelSchema = z.string().openapi({
  description: 'A label consisting of any characters',
  example: 'Math Homework 1 - Due 2023/12/31!',
});

export const TagSchema = z
  .string()
  .regex(/^[a-z0-9]+$/, 'Tag must be lowercase alphanumeric')
  .min(0)
  .max(128)
  .openapi({
    description: 'A lowercase alphanumeric tag',
    example: 'homework',
  });

export const CommaSeparatedTagsSchema = z
  .string()
  .regex(/^$|^([a-z0-9]+,)*[a-z0-9]+$/)
  .min(0)
  .max(2560)
  .openapi({
    description: 'A comma-separated list of lowercase alphanumeric tags',
    example: 'math,homework,algebra',
  });

export const StringBooleanSchema = z.union([z.literal('true'), z.literal('false')]).openapi({
  description: 'A string representing a boolean value ("true" or "false")',
  example: 'true',
});

export const StringBooleanToBooleanSchema = z.union([
  z.boolean(),
  StringBooleanSchema.transform(val => val === 'true'),
]);

export const LimitQuerySchema = z
  .union([z.literal('all'), z.coerce.number().int().positive()])
  .openapi({
    description:
      "Limit the number of records returned. Pass 'all' (default) to fetch every match, or a positive integer to cap the result count.",
    example: 10,
  });

export const GenericTipTapJsonSchema = z
  .object({
    type: z.literal('doc'),
    content: z.array(z.any()),
  })
  .openapi({
    description: 'A TipTap JSON document structure',
    example: {
      type: 'doc',
      content: [
        {
          type: 'paragraph',
          content: [
            {
              type: 'text',
              text: 'Example text',
            },
          ],
        },
      ],
    },
  });

/**
 * Natural Human Attribute Schemas
 */

export const EmailSchema = z.email('Invalid email format').openapi({
  description: 'A valid email address',
  example: 'johndoe@example.com',
});

export const PhoneNumberSchema = z
  .string()
  .transform(val => {
    const spacelessPhoneNumber = val.replace(/ /g, '').replace(/-/g, '');
    if (spacelessPhoneNumber.startsWith('+')) return spacelessPhoneNumber;
    if (spacelessPhoneNumber.startsWith('0')) return `+61${spacelessPhoneNumber.slice(1)}`;
    return spacelessPhoneNumber;
  })
  .pipe(z.string().regex(/^\+61[0-9]{9}$/, 'Invalid phone number format'))
  .openapi({
    description: 'An Australian phone number in E.164 format',
    example: '+61412345678',
  });

export const HumanNameSchema = z
  .string()
  .min(1, 'Name is too short')
  .max(64, 'Name is too long')
  .regex(/^[A-Za-z0-9\-'()., ]+$/, 'Name contains invalid characters')
  .openapi({
    description:
      'A human name consisting of alphabetic characters, digits, spaces, hyphens, apostrophes, parentheses, dots, and commas',
    example: "O'Connor-Smith",
  });

export const HumanFullNameSchema = z
  .string()
  .min(1, 'Name is too short')
  .max(128, 'Name is too long')
  .regex(/^[A-Za-z0-9\-'()., ]+$/, 'Name contains invalid characters')
  .openapi({
    description: 'A full human name',
    example: "John O'Connor-Smith",
  });

export const BirthdaySchema = UnixTimestampSchema.refine(
  val => val < dayjs().valueOf(),
  'Birthday cannot be in the future'
).openapi({
  description: 'A birthday represented as a Unix timestamp in milliseconds',
  example: 915148800000, // 1999-01-01
});

export const ProfileIconSchema = z
  .union([
    z.object({
      color: HexColorSchema,
      image: HttpUrlSchema,
    }),
    z.object({
      color: HexColorSchema,
    }),
    z.object({
      image: HttpUrlSchema,
    }),
  ])
  .openapi({
    description: 'A profile icon consisting of a color and/or image URL',
    example: {
      color: '#0c374d',
      image: 'https://example.com/profile.png',
    },
  });

export const SchoolNameSchema = z
  .string()
  .regex(/^[A-Za-z0-9\-'\u2018\u2019., ()]+$/)
  .min(1)
  .max(256);

export const GradYearSchema = z
  .number({ message: 'please enter a year' })
  .int({ message: 'must be a whole year' })
  .min(2018, { message: 'must be between 2018 and 2100' })
  .max(2100, { message: 'must be between 2018 and 2100' })
  .openapi({
    description: 'A graduation year between 2018 and 2100',
    example: 2025,
  });

export const StreetAddressSchema = z.string().min(1).max(512).openapi({
  description: 'A street address',
  example: '123 Example St, Suburbia',
});

export const CountrySchema = z
  .string()
  .transform(val => {
    return val.replace(/\b\w/g, char => char.toUpperCase());
  })
  .pipe(z.string().min(2).max(128))
  .openapi({
    description: 'A country name',
    example: 'Australia',
  });

export const PostcodeSchema = z
  .string()
  .refine(x => !x || Number(x))
  .openapi({
    description: 'A postcode (numeric string)',
    example: '3000',
  });

/**
 * User Grouping Schemas
 */

export const UserGroupSchema = z
  .enum(UserGroup)
  .openapi({
    description: 'A user group',
    example: UserGroup.Students,
  })
  .register(enumLabels, { labels: USER_GROUP_LABELS });

export const StaffUserGroupSchema = z
  .enum(StaffUserGroups)
  .openapi({
    description: 'A staff user group',
    example: UserGroup.Admins,
  })
  .register(enumLabels, {
    labels: Object.fromEntries(StaffUserGroups.map(group => [group, USER_GROUP_LABELS[group]])),
  });

export const StudentUserGroupSchema = z.enum([UserGroup.Students]).openapi({
  description: 'A student user group',
  example: UserGroup.Students,
});

export const UserTypeSchema = z.enum(UserType).openapi({
  description: 'A user type',
  example: UserType.Student,
});

/**
 * Organisation Domain Schemas
 */

export const LocationSchema = z
  .enum(Location)
  .openapi({
    description: 'A branch location',
    example: Location.Chatswood,
  })
  .register(enumLabels, { labels: LOCATION_LABELS });

export const GradeSchema = z
  .enum(Grade)
  .openapi({
    description: 'A school grade',
    example: Grade.HSC,
  })
  .register(enumLabels, { labels: GRADE_LABELS });

export const SubjectLabelSchema = z.enum(SubjectLabel).openapi({
  description: 'A school subject label',
  example: SubjectLabel.MathsExt1,
});

export const SubjectSchema = z
  .enum(Subject)
  .openapi({
    description: 'A school subject code',
    example: Subject.MathsExt1,
  })
  .register(enumLabels, { labels: SUBJECT_LABELS });

export const XeroDepartmentSchema = z.enum(XeroDepartment).openapi({
  description: 'A Xero department',
  example: XeroDepartment.Mathematics,
});

export const SubjectDetailsSchema = z
  .object({
    key: SubjectSchema,
    value: SubjectSchema,
    label: SubjectLabelSchema,
    xeroDepartment: z.object({
      name: z.string(),
      option: XeroDepartmentSchema,
    }),
  })
  .openapi({
    description: 'Details of a subject',
    example: {
      key: Subject.MathsExt1,
      value: Subject.MathsExt1,
      label: 'Maths Ext 1',
      xeroDepartment: {
        name: 'Department',
        option: XeroDepartment.Mathematics,
      },
    },
  });

/**
 * Application Settings Schemas
 */

export const ThemeModeSchema = z.enum(ThemeMode).openapi({
  description: 'A theme mode',
  example: ThemeMode.Dark,
});

/**
 * Slack Schemas
 */

export const SlackUserIdSchema = z
  .string()
  .regex(/^U[A-Z0-9]{8,}$/, 'Invalid Slack user ID format')
  .openapi({
    description: 'A Slack user ID',
    example: 'U12345678',
  });
export const SlackChannelIdSchema = z
  .string()
  .regex(/^C[A-Z0-9]{8,}$/, 'Invalid Slack channel ID format')
  .openapi({
    description: 'A Slack channel ID',
    example: 'C12345678',
  });

/**
 * Xero Schemas
 */

export const XeroCodeSchema = z
  .object({
    amount: z.number().min(0).optional().default(0),
    code: z.string().min(0).max(32),
  })
  .openapi({
    description: 'A Xero tracking code with an associated amount',
    example: {
      amount: 100,
      code: 'ABC123',
    },
  });

export const XeroUserIdSchema = z.string().min(1).max(256).openapi({
  description: 'A Xero user ID',
  example: 'James Smith (31412231312)',
});
